import React, { ReactNode } from "react";
import { Scoped } from "kremling";

import { CpSelect } from "../cp-select/cp-select.component";
import {
  defaultSearchFilter,
  filterAlpha,
  SelectDataItem,
} from "../cp-select/common/utils";
import styles from "./../cp-select/cp-select.styles.scss";

export type CpSelectMultiInputProps<TData, TValue> = {
  allowTextWrap?: boolean;
  appendTo?: HTMLElement | "parent";
  className?: string;
  data?: TData[];
  disabled?: boolean;
  isGroupData?: boolean;
  groupType?: "header" | "border";
  loading?: boolean;
  onBlur?: () => void;
  onChange: (value: NoInfer<TValue>[]) => void;
  onClose?: () => void;
  onOpen?: () => void;
  teamMember?: boolean;
  placeholder?: string;
  preventKeyOpen?: boolean;
  preventOutsideClickUntilClosed?: boolean;
  renderFooter?: (params: {
    onChange: (value: TValue[]) => void;
    close: () => void;
  }) => ReactNode;
  renderPill?: (value: SelectDataItem[]) => ReactNode;
  searchFilter?: boolean | ((item: SelectDataItem) => boolean);
  searchOnChange?: (value: string) => void;
  searchValue?: string;
  clearOnSelect?: boolean;
  stopPropagation?: boolean;
  transformData?: (item: TValue) => SelectDataItem;
  triggerRef?: React.RefObject<HTMLElement>;
  value: TValue[];
  error?: boolean | string;
};

function CpSelectMultiInputBase<TData, TValue>(
  props: CpSelectMultiInputProps<TData, TValue>,
) {
  const {
    allowTextWrap,
    appendTo,
    className,
    data,
    disabled,
    isGroupData,
    groupType,
    loading,
    onBlur,
    onChange,
    onClose,
    onOpen,
    teamMember,
    placeholder,
    preventKeyOpen,
    preventOutsideClickUntilClosed,
    renderFooter,
    renderPill,
    searchFilter,
    searchOnChange,
    searchValue,
    clearOnSelect,
    stopPropagation,
    transformData,
    triggerRef,
    value,
    error,
  } = props;

  return (
    <Scoped css={styles}>
      <CpSelect<TData, TValue>
        allowTextWrap={allowTextWrap}
        appendTo={appendTo}
        className={className}
        contentWidth="block"
        data={data}
        disabled={disabled}
        isGroupData={isGroupData}
        groupType={groupType}
        loading={loading}
        onChange={onChange as (value: TValue | TValue[]) => void}
        onClose={onClose}
        onOpen={onOpen}
        preventKeyOpen={preventKeyOpen}
        preventOutsideClickUntilClosed={preventOutsideClickUntilClosed}
        renderFooter={renderFooter || (CpSelect as any).Footer}
        renderGroup={(CpSelect as any).Group}
        renderItem={(CpSelect as any).ItemMulti}
        renderTrigger={(triggerProps) => {
          const TriggerComponent = (CpSelect as any).TriggerMultiInput;
          return (
            <TriggerComponent
              renderPill={renderPill}
              onBlur={onBlur}
              teamMember={teamMember}
              placeholder={placeholder}
              error={error}
              {...triggerProps}
            />
          );
        }}
        searchFilter={defaultSearchFilter(searchFilter, searchValue)}
        searchOnChange={searchOnChange}
        searchValue={searchValue}
        clearOnSelect={clearOnSelect}
        showResultsInContent
        stopPropagation={stopPropagation}
        transformData={transformData}
        triggerIsBlock
        triggerRef={triggerRef}
        value={value}
      />
      {typeof error === "string" && (
        <div className="cp-select--error-text">{error}</div>
      )}
    </Scoped>
  );
}

// Export type that includes static method
export type CpSelectMultiInputComponent = (<TData, TValue>(
  props: CpSelectMultiInputProps<TData, TValue>,
) => React.JSX.Element) & {
  filterAlpha: typeof filterAlpha;
};

// Create typed component with static properties
const CpSelectMultiInput =
  CpSelectMultiInputBase as CpSelectMultiInputComponent;

// Attach static method
CpSelectMultiInput.filterAlpha = filterAlpha;

// Export the properly typed component
export { CpSelectMultiInput };
